import React, { Fragment } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { HEALTH_COLORS, getHealthStatus, getHealthDetails, getWorstHealthStatus } from './healthUtils'

// Function to convert camelCase to sentence case
const normalizeVariableName = (variableName) => {
  // Handle common abbreviations and special cases
  const specialCases = {
    'heLevel': 'He Level',
    'hePressure': 'He Pressure', 
    'roomTemp': 'Room Temperature',
    'roomHumidity': 'Room Humidity',
    'waterFlow': 'Water Flow',
    'waterTemp': 'Water Temperature',
    'compressorStatus': 'Compressor Status',
    'heLevelTime': 'He Level Time'
  }
  
  if (specialCases[variableName]) {
    return specialCases[variableName]
  }
  
  // General camelCase to sentence case conversion
  return variableName
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
    .trim()
}

const HealthIndicator = styled.div`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => HEALTH_COLORS[props.status] || HEALTH_COLORS.unknown};
  margin-right: 8px;
  border: 2px solid white;
  box-shadow: 0 0 4px rgba(0,0,0,0.3);
`

const HealthSection = styled.div`
  margin-top: 8px;
  padding: 8px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid ${props => HEALTH_COLORS[props.status] || HEALTH_COLORS.unknown};
`

const MetricItem = styled.div`
  font-size: 11px;
  margin: 2px 0;
  color: #666;
`

const ModalitySection = styled.div`
  margin-bottom: 12px;
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #fafafa;
`

const ModalityHeader = styled.div`
  font-weight: bold;
  margin-bottom: 6px;
  color: #333;
`

const LocationInfo = React.memo(({ info }) => {
  // Check if this is a group of modalities or a single modality
  const isGroup = info.modalities && Array.isArray(info.modalities)
  const modalities = isGroup ? info.modalities : [info]
  const worstStatus = isGroup ? getWorstHealthStatus(modalities) : getHealthStatus(info)
  
  return (
    <Container>
      {info.type === 'fixed' ? (
        <Fragment>
          <b>{info.siteName}</b>
          <br />
          {info.siteAddress}
          <br />
          <br />
          {info.url && (
            <a href={info.url} target="_blank" rel="noopener noreferrer">
              open
            </a>
          )}
        </Fragment>
      ) : (
        <Fragment>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <HealthIndicator status={worstStatus} />
            <b>
              {isGroup ? `${modalities[0].siteName} (${modalities.length} modalities)` : info.siteName} 
              {!isGroup && ` | ${info.siteIdNumber}`}
            </b>
          </div>
          {modalities[0].siteAddress}
          <br />
          <br />
          
          {isGroup ? (
            // Display multiple modalities
            modalities.map((modality, index) => {
              const healthStatus = getHealthStatus(modality)
              const healthDetails = getHealthDetails(modality)
              
              return (
                <ModalitySection key={modality.id || index}>
                  <ModalityHeader>
                    {modality.manufacturer} {modality.modality} - {modality.model}
                    <span style={{ 
                      color: HEALTH_COLORS[healthStatus] || HEALTH_COLORS.unknown,
                      fontWeight: 'bold',
                      marginLeft: '8px',
                      textTransform: 'capitalize'
                    }}>
                      ({healthStatus})
                    </span>
                  </ModalityHeader>
                  
                  <div style={{ fontSize: '11px', marginBottom: '4px' }}>
                    <b>Serial #:</b> {modality.serialNumber}
                  </div>
                  
                  {healthDetails.metrics && (
                    <MetricItem>
                      <b>Metrics:</b> {healthDetails.metrics}
                    </MetricItem>
                  )}
                  
                  {healthDetails.keys && healthDetails.keys.length > 0 && (
                    <MetricItem>
                      <b>Issues:</b> {healthDetails.keys.map(key => normalizeVariableName(key)).join(', ')}
                    </MetricItem>
                  )}
                  
                  {healthDetails.matches && Object.keys(healthDetails.matches).length > 0 && (
                    <div style={{ marginTop: '4px' }}>
                      {Object.entries(healthDetails.matches).map(([level, keys]) => (
                        <MetricItem key={level}>
                          <span style={{ color: HEALTH_COLORS[level] }}>
                            {level.toUpperCase()}: {keys.map(key => normalizeVariableName(key)).join(', ')}
                          </span>
                        </MetricItem>
                      ))}
                    </div>
                  )}
                  
                  <div style={{ marginTop: '6px' }}>
                    <Link to={`/modalities/${modality.id}/show`} style={{ fontSize: '11px' }}>
                      View Details
                    </Link>
                  </div>
                </ModalitySection>
              )
            })
          ) : (
            // Display single modality (original logic)
            <Fragment>
              <b>Modality:</b> {info.manufacturer} {info.modality}
              <br />
              <b>Model:</b> {info.model}
              <br />
              <b>Serial #:</b> {info.serialNumber}
              <br />
              <b>Status:</b> <span style={{ 
                color: HEALTH_COLORS[worstStatus] || HEALTH_COLORS.unknown,
                fontWeight: 'bold',
                textTransform: 'capitalize'
              }}>
                {worstStatus}
              </span>
              
              {(() => {
                const healthDetails = getHealthDetails(info)
                return (
                  <HealthSection status={worstStatus}>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Health Details:</div>
                    
                    {healthDetails.metrics && (
                      <MetricItem>
                        <b>Metrics:</b> {healthDetails.metrics}
                      </MetricItem>
                    )}
                    
                    {healthDetails.keys && healthDetails.keys.length > 0 && (
                      <MetricItem>
                        <b>Issues:</b> {healthDetails.keys.map(key => normalizeVariableName(key)).join(', ')}
                      </MetricItem>
                    )}
                    
                    {healthDetails.timeStatus && (
                      <MetricItem>
                        <b>Last Update:</b> {healthDetails.timeStatus.message}
                      </MetricItem>
                    )}
                    
                    {healthDetails.matches && Object.keys(healthDetails.matches).length > 0 && (
                      <div style={{ marginTop: '4px' }}>
                        {Object.entries(healthDetails.matches).map(([level, keys]) => (
                          <MetricItem key={level}>
                            <span style={{ color: HEALTH_COLORS[level] }}>
                              {level.toUpperCase()}: {keys.map(key => normalizeVariableName(key)).join(', ')}
                            </span>
                          </MetricItem>
                        ))}
                      </div>
                    )}
                  </HealthSection>
                )
              })()}
              
              <br />
              <Link to={`/modalities/${info.id}/show`}>open</Link>
            </Fragment>
          )}
        </Fragment>
      )}
    </Container>
  )
})

const Container = styled.div`
  font-family: Roboto, Helvetica, sans-serif;
  color: #555;
  font-size: 0.9em;
  padding: 10px;
  min-width: 280px;
  max-width: 400px;
  max-height: 500px;
  overflow-y: auto;
`

export default LocationInfo
